import React from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { useCurrency } from "../../context/CurrencyContext";
import { useMember } from "../../context/MemberAuthContext";
import CurrencySelector from "./CurrencySelector";
import PaystackButton from "./PaystackButton";

const inputCls =
  "w-full border border-parchment rounded-md px-4 py-3 outline-none focus:border-gold focus:ring-1 focus:ring-gold bg-white text-ink";

const PRESETS = [25, 50, 100, 250];

export const PayPalDonate = ({ projects = [] }) => {
  const { currency, rates, convert, format, isConverted, ratesUpdatedText } = useCurrency();
  const [config, setConfig] = React.useState(null);
  const [form, setForm] = React.useState({ name: "", email: "", amount: "", project: "General Support", message: "" });
  const [frequency, setFrequency] = React.useState("one-time");
  const [showOnWall, setShowOnWall] = React.useState(true);
  const [done, setDone] = React.useState(false);
  const [pledgeLoading, setPledgeLoading] = React.useState(false);
  const { member } = useMember();
  React.useEffect(() => {
    if (!member) return;
    setForm((p) => ({ ...p, name: p.name || member.name || "", email: p.email || member.email || "" }));
  }, [member]);

  React.useEffect(() => {
    api.get("/paypal/config").then((r) => setConfig(r.data)).catch(() => setConfig({ configured: false }));
  }, []);

  const rate = rates[currency] || 1;
  const amountLocal = parseFloat(String(form.amount).replace(/[^0-9.]/g, "")) || 0;
  const amountUSD = amountLocal / rate;                       // charge currency is always USD
  const amountUSDStr = amountUSD > 0 ? amountUSD.toFixed(2) : "";
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const valid = amountUSD > 0 && form.name.trim() && emailOk;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Fallback pledge submission (used when PayPal isn't configured)
  const submitPledge = async (e) => {
    e.preventDefault();
    setPledgeLoading(true);
    try {
      const { data } = await api.post("/donation", {
        name: form.name,
        email: form.email,
        amount: amountUSDStr,
        frequency: frequency === "monthly" ? "Recurring (Monthly)" : "One Time",
        project: form.project,
        message: form.message,
        public: showOnWall,
      });
      toast.success(data.message || "Thank you for your support!");
      setForm({ name: "", email: "", amount: "", project: "General Support", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setPledgeLoading(false);
    }
  };

  if (!config) {
    return (
      <div className="bg-cream border border-parchment rounded-2xl p-8 flex items-center gap-3 text-ink-muted" data-testid="donate-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading donation options…
      </div>
    );
  }

  if (done) {
    return (
      <div className="bg-cream border border-parchment rounded-2xl p-10 text-center" data-testid="donate-success">
        <CheckCircle2 className="w-14 h-14 text-forest mx-auto mb-4" />
        <h3 className="font-serif text-3xl text-ink mb-2">Ẹ ṣé púpọ̀! Thank you.</h3>
        <p className="text-ink-muted mb-6">
          Your {frequency === "monthly" ? "monthly" : "one-time"} donation to ÀsÀNigHT Worldwide has been received.
          Your support helps keep the Yorùbá language and heritage alive.
        </p>
        <button onClick={() => { setDone(false); setForm({ name: "", email: "", amount: "", project: "General Support", message: "" }); }} data-testid="donate-again-button" className="text-forest font-semibold underline">
          Make another donation
        </button>
      </div>
    );
  }

  const sharedFields = (
    <>
      <div>
        <label className="block text-sm font-medium text-ink mb-1.5">Full Name</label>
        <input required value={form.name} onChange={(e) => setField("name", e.target.value)} data-testid="donation-name-input" className={inputCls} />
      </div>
      <div>
        <label className="block text-sm font-medium text-ink mb-1.5">Email</label>
        <input type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} data-testid="donation-email-input" className={inputCls} />
      </div>
      <div>
        <div className="flex items-center justify-between gap-3 mb-1.5">
          <label className="block text-sm font-medium text-ink">Donation Amount</label>
          <CurrencySelector variant="light" />
        </div>
        <div className="flex flex-wrap gap-2 mb-2">
          {PRESETS.map((usd) => {
            const local = Math.max(1, Math.round(convert(usd)));
            const active = Math.round(amountLocal) === local;
            return (
              <button
                type="button"
                key={usd}
                onClick={() => setField("amount", String(local))}
                data-testid={`donation-preset-${usd}`}
                className={`px-4 py-2 rounded-full border text-sm font-semibold transition-colors ${
                  active ? "bg-forest text-cream border-forest" : "border-parchment text-ink hover:border-forest"
                }`}
              >
                {format(usd)}
              </button>
            );
          })}
        </div>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-muted text-sm font-semibold">{currency}</span>
          <input
            type="number"
            min="1"
            step="1"
            required
            value={form.amount}
            onChange={(e) => setField("amount", e.target.value)}
            placeholder="Enter amount"
            data-testid="donation-amount-input"
            className={inputCls + " pl-14"}
          />
        </div>
        {isConverted && amountUSD > 0 && (
          <p className="text-xs text-forest mt-2" data-testid="donation-usd-note">
            You'll be charged <span className="font-semibold">${amountUSDStr} USD</span> — {format(amountUSD)} is shown at today's rate.
          </p>
        )}
        {isConverted && ratesUpdatedText && (
          <p className="text-[11px] text-ink-muted mt-1 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-forest inline-block" /> {ratesUpdatedText}
          </p>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">Frequency</label>
          <div className="flex rounded-md border border-parchment overflow-hidden">
            <button type="button" onClick={() => setFrequency("one-time")} data-testid="freq-one-time" className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${frequency === "one-time" ? "bg-forest text-cream" : "bg-white text-ink"}`}>
              One-time
            </button>
            <button type="button" onClick={() => setFrequency("monthly")} data-testid="freq-monthly" className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${frequency === "monthly" ? "bg-forest text-cream" : "bg-white text-ink"}`}>
              Monthly
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">Project</label>
          <select value={form.project} onChange={(e) => setField("project", e.target.value)} data-testid="donation-project-select" className={inputCls + " bg-white"}>
            <option>General Support</option>
            {projects.map((p) => (<option key={p.title}>{p.title}</option>))}
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-ink mb-1.5">Message (optional)</label>
        <textarea rows={2} value={form.message} onChange={(e) => setField("message", e.target.value)} data-testid="donation-message-input" className={inputCls} />
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="donation-public-toggle">
        <input
          type="checkbox"
          checked={showOnWall}
          onChange={(e) => setShowOnWall(e.target.checked)}
          data-testid="donation-public-checkbox"
          className="mt-1 w-4 h-4 accent-forest cursor-pointer"
        />
        <span className="text-sm text-ink-muted leading-snug">
          Show my first name on the <span className="text-ink font-medium">Recent Supporters</span> wall.
          <span className="block text-xs mt-0.5">Only your first name and a donation range are shown — never your email or exact amount. Uncheck to stay anonymous.</span>
        </span>
      </label>
    </>
  );

  // PayPal not configured → graceful pledge fallback
  if (!config.configured) {
    return (
      <form onSubmit={submitPledge} className="bg-cream border border-parchment rounded-2xl p-8 space-y-5 shadow-xl shadow-ink/5" data-testid="donate-pledge-form">
        {sharedFields}
        {frequency === "one-time" && valid && (
          <PaystackButton
            variant="light"
            initialize={async (cur) => {
              const { data } = await api.post("/paystack/donation/initialize", { amount: amountUSDStr, currency: cur, email: form.email, name: form.name, project: form.project, message: form.message, public: showOnWall, origin_url: window.location.origin });
              return data.authorization_url;
            }}
          />
        )}
        <button type="submit" disabled={pledgeLoading} data-testid="donation-submit-button" className="w-full bg-gold text-night font-semibold py-4 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
          {pledgeLoading ? "Submitting..." : "Pledge My Support"}
        </button>
      </form>
    );
  }

  const isMonthly = frequency === "monthly";
  const ppOptions = isMonthly
    ? { "client-id": config.client_id, currency: "USD", intent: "subscription", vault: true }
    : { "client-id": config.client_id, currency: "USD", intent: "capture" };

  return (
    <div className="bg-cream border border-parchment rounded-2xl p-8 space-y-5 shadow-xl shadow-ink/5" data-testid="donate-paypal-form">
      {sharedFields}

      {!valid ? (
        <p className="text-sm text-ink-muted bg-sand border border-parchment rounded-lg px-4 py-3" data-testid="donate-hint">
          Enter your name, a valid email and a donation amount to continue to PayPal.
        </p>
      ) : (
        <div data-testid="paypal-buttons-wrap" className="pt-1">
          <PayPalScriptProvider key={frequency} options={ppOptions}>
            {isMonthly ? (
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "pill", label: "subscribe" }}
                forceReRender={[form.amount, frequency, form.name, form.email]}
                createSubscription={async (data, actions) => {
                  const { data: plan } = await api.post("/paypal/create-plan", { amount: amountUSDStr });
                  return actions.subscription.create({ plan_id: plan.plan_id });
                }}
                onApprove={async (data) => {
                  await api.post("/paypal/record-subscription", {
                    subscription_id: data.subscriptionID,
                    amount: amountUSDStr,
                    name: form.name,
                    email: form.email,
                    project: form.project,
                    message: form.message,
                    public: showOnWall,
                  });
                  setDone(true);
                }}
                onError={(err) => { console.error(err); toast.error("Something went wrong with PayPal. Please try again."); }}
              />
            ) : (
              <PayPalButtons
                style={{ layout: "vertical", color: "gold", shape: "pill", label: "donate" }}
                forceReRender={[form.amount, frequency, form.name, form.email]}
                createOrder={async () => {
                  const { data } = await api.post("/paypal/create-order", {
                    amount: amountUSDStr,
                    name: form.name,
                    email: form.email,
                    project: form.project,
                    message: form.message,
                    public: showOnWall,
                  });
                  return data.id;
                }}
                onApprove={async (data) => {
                  const { data: res } = await api.post(`/paypal/capture-order/${data.orderID}`);
                  if (res.status === "COMPLETED") setDone(true);
                  else toast.error("Payment was not completed.");
                }}
                onError={(err) => { console.error(err); toast.error("Something went wrong with PayPal. Please try again."); }}
              />
            )}
          </PayPalScriptProvider>
        </div>
      )}

      {frequency === "one-time" && valid && (
        <PaystackButton
          variant="light"
          initialize={async (cur) => {
            const { data } = await api.post("/paystack/donation/initialize", { amount: amountUSDStr, currency: cur, email: form.email, name: form.name, project: form.project, message: form.message, public: showOnWall, origin_url: window.location.origin });
            return data.authorization_url;
          }}
        />
      )}
    </div>
  );
};

export default PayPalDonate;
